/* Copyright (c) 2020-present CloudIO Inc */

import { Box, Theme } from '@mui/material';
import { CSSProperties } from '@mui/styles';
import { SxProps } from '@mui/system';
import { ThemeColor } from 'componentregistry';
import { IconType } from 'componentregistry/icons/types';
import React, { useCallback, useMemo } from 'react';
import { ReactSVG } from 'react-svg';
import { useTheme } from '@mui/material/styles';
import { getTextColor, getColor } from 'theme/useThemeColor';
import { isNotEmpty } from 'utils/isEmpty';

type IconStyleProps = {
  parentColor?: ThemeColor | string | null;
  iconColor?: ThemeColor | 'inherit' | string | null;
};

const getIconColor = (
  theme: Theme,
  { parentColor, iconColor }: IconStyleProps,
) => {
  if (parentColor) {
    return getColor(theme, parentColor);
  }
  return getTextColor(theme, iconColor);
};

export function parseIconText(value: string | undefined | null): {
  iconName?: string;
  iconType?: IconType;
} {
  if (value) {
    const typeVal = value.split(':');
    const iconName = typeVal[0];
    const iconType = typeVal.length === 2 ? (typeVal[1] as IconType) : 'light';
    return { iconType, iconName };
  }
  return {};
}

type IconProps = {
  iconName: string | undefined;
  iconType?: IconType;
  height?: number;
  width?: number;
  color?: ThemeColor | 'inherit' | string | null;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  parentColor?: ThemeColor | string | null;
  className?: string;
  style?: CSSProperties;
  iconButton?: boolean;
  tooltip?: string | undefined | null;
  toolTipPosition?: string;
  toolTipMaxWidth?: number;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  'data-tip'?: string | null;
  'aria-label'?: string;
};

const Icon = (
  {
    iconName,
    iconType = 'light',
    onClick,
    onMouseEnter,
    onMouseLeave,
    onMouseDown,
    onKeyDown,
    parentColor,
    color,
    className,
    style,
    iconButton = false,
    height = iconButton ? 24 : 16,
    width = iconButton ? 24 : 16,
    tooltip,
    toolTipPosition,
    toolTipMaxWidth,
    sx,
    disabled,
    ...others
  }: IconProps,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement | null => {
  const cursor = onClick && !disabled ? 'pointer' : 'unset';
  const xstyle = useMemo(
    () => ({
      cursor,
      width,
      height,
      display: 'inline-block',
    }),
    [cursor, height, width],
  );

  const beforeInjection = useCallback(
    (svg: SVGSVGElement) => {
      svg.setAttribute('height', `${height}`);
      svg.setAttribute('width', `${width}`);
      svg.style.verticalAlign = 'top';
    },
    [height, width],
  );

  const fallback = useCallback(() => <svg data-icon={iconName} />, [iconName]);
  const theme = useTheme();

  if (!(iconType && iconName)) {
    return null;
  }

  const src = `svgs/${iconType}/${iconName}.svg`;

  const tip = isNotEmpty(tooltip)
    ? {
        'data-tip': tooltip,
        'data-tip-at': toolTipPosition,
        'data-tip-delay': 0,
        'data-tip-width': toolTipMaxWidth,
      }
    : undefined;

  if (disabled) {
    color = 'disabled';
    parentColor = undefined;
  }

  const colr =
    !parentColor && color === 'inherit'
      ? undefined
      : getIconColor(theme, { parentColor, iconColor: color });

  const _sx = {
    height,
    width,
    pointerEvents: !(
      onClick ||
      onMouseEnter ||
      onMouseDown ||
      onKeyDown ||
      isNotEmpty(tooltip) ||
      isNotEmpty(others['data-tip'])
    )
      ? 'none'
      : 'auto',
    ...sx,
    fontSize: 'unset',
    overflow: 'hidden' as const,
    ...style,
    cursor,
    '& svg': !colr
      ? {
          fill: 'currentcolor',
          stroke: 'currentcolor',
          verticalAlign: 'top',
        }
      : {
          fill: colr,
          color: colr,
          stroke: colr,
          verticalAlign: 'top',
        },
    '& span, & svg': {
      pointerEvents: 'none',
    },
  } as SxProps<Theme>;

  if (process.env.NODE_ENV === 'test') {
    return <div />;
  }

  return (
    <Box
      ref={ref}
      component="span"
      role="button"
      className={className}
      sx={_sx}
      {...tip}
      {...others}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={disabled ? undefined : onMouseEnter}
      onMouseLeave={disabled ? undefined : onMouseLeave}
      onKeyDown={disabled ? undefined : onKeyDown}
      onMouseDown={disabled ? undefined : onMouseDown}
      data-icon-name={iconName}
      data-icon-type={iconType}
      data-icon-color={color}
      data-icon-parent-color={parentColor}
      aria-label={tooltip ? tooltip : others['aria-label']}
    >
      <ReactSVG
        src={src}
        style={xstyle}
        beforeInjection={beforeInjection}
        // className={classes.iconFill}
        wrapper="span"
        fallback={fallback}
      />
    </Box>
  );
};

export default React.forwardRef(Icon);
